import { useEffect, useRef, useState } from "react";
import type { AiGlobalSettings, DataStatus } from "@currencycore/shared";
import {
  fetchRatesForDate,
  generateHomepageAI,
  getDataStatus,
  getAiSettings,
  setAiSettings,
  syncPpp,
  syncRates,
  uploadPpp,
  uploadRates,
  type PppSyncResult,
  type RateSyncResult,
} from "../lib/api.ts";
import { ApiRequestError } from "../lib/api.ts";
import { toast } from "../components/Toast.tsx";
import { DatePicker } from "../components/DatePicker.tsx";

const nf = (n: number) => n.toLocaleString("en-US");
const todayIso = () => new Date().toISOString().slice(0, 10);

export function DataPage() {
  const [status, setStatus] = useState<DataStatus | null>(null);
  const [busy, setBusy] = useState<"rates" | "rates-date" | "ppp" | "upload" | "upload-rates" | "homepage-ai" | null>(null);
  const [lastRates, setLastRates] = useState<RateSyncResult | null>(null);
  const [lastPpp, setLastPpp] = useState<PppSyncResult | null>(null);
  const [fetchDate, setFetchDate] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const ratesFileRef = useRef<HTMLInputElement>(null);
  const [forceRates, setForceRates] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [aiMsg, setAiMsg] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiLoaded, setAiLoaded] = useState(false);

  async function refresh() {
    try {
      setStatus(await getDataStatus());
    } catch {
      /* status is best-effort */
    }
  }

  async function loadAi() {
    try {
      const s = await getAiSettings();
      setAiEnabled(s.enabled);
      setAiMsg(s.message);
    } catch {
      /* settings are best-effort */
    } finally {
      setAiLoaded(true);
    }
  }

  async function saveAi() {
    setAiBusy(true);
    try {
      const s = await setAiSettings({ enabled: aiEnabled, message: aiMsg });
      setAiEnabled(s.enabled);
      setAiMsg(s.message);
      toast(s.enabled ? "AI endpoint enabled platform-wide." : "AI endpoint disabled platform-wide.", true);
    } catch (e) {
      toast(errMsg(e, "Could not update AI settings."));
    } finally {
      setAiBusy(false);
    }
  }

  useEffect(() => {
    void refresh();
    void loadAi();
  }, []);

  async function runRates() {
    setBusy("rates");
    try {
      const r = await syncRates();
      setLastRates(r);
      toast(`Rates updated: ${r.currencyCount} currencies for ${r.latestDate}.`, true);
      await refresh();
    } catch (e) {
      toast(errMsg(e, "Rate sync failed."));
    } finally {
      setBusy(null);
    }
  }

  async function runFetchForDate() {
    const date = fetchDate.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      toast("Pick a date (YYYY-MM-DD) first.");
      return;
    }
    if (date > todayIso()) {
      toast("Date cannot be in the future.");
      return;
    }
    setBusy("rates-date");
    try {
      const r = await fetchRatesForDate(date);
      setLastRates(r);
      if (r.written.includes(date)) {
        toast(`Stored rates for ${date} (${r.currencyCount} currencies).`, true);
      } else {
        toast(`Rates already present for ${date}; skipped.`, true);
      }
      await refresh();
    } catch (e) {
      toast(errMsg(e, "Fetch failed."));
    } finally {
      setBusy(null);
    }
  }

  async function runPpp() {
    setBusy("ppp");
    try {
      const r = await syncPpp();
      setLastPpp(r);
      toast(`PPP synced: ${r.changed} changed of ${r.eligible}.`, true);
      await refresh();
    } catch (e) {
      toast(errMsg(e, "PPP sync failed."));
    } finally {
      setBusy(null);
    }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("upload");
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const r = await uploadPpp(json);
      setLastPpp(r);
      toast(`Uploaded: ${r.changed} changed of ${r.eligible} rows.`, true);
      await refresh();
    } catch (e) {
      toast(e instanceof SyntaxError ? "That file isn't valid JSON." : errMsg(e, "Upload failed."));
    } finally {
      setBusy(null);
    }
  }

  async function onRatesFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("upload-rates");
    try {
      const json = JSON.parse(await file.text());
      const r = await uploadRates(json, forceRates);
      setLastRates(r);
      toast(
        `Uploaded ${r.written.length} date(s) · ${nf(r.rowsWritten)} rows · latest ${r.latestDate}` +
          (r.skipped.length ? ` · ${r.skipped.length} skipped` : ""),
        true,
      );
      await refresh();
    } catch (e) {
      toast(e instanceof SyntaxError ? "That file isn't valid JSON." : errMsg(e, "Rates upload failed."));
    } finally {
      setBusy(null);
    }
  }

  async function runHomepageAI() {
    setBusy("homepage-ai");
    try {
      const r = await generateHomepageAI();
      toast(r.message, true);
    } catch (e) {
      toast(errMsg(e, "Homepage AI generation failed."));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Data management</h1>
          <p className="muted">Trigger data jobs and inspect the managed datasets.</p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-ghost btn-sm" onClick={refresh}>Refresh</button>
        </div>
      </div>

      {/* Status counters */}
      <div className="grid grid-4 stat-grid">
        <StatCard label="Latest rate date" value={status?.rates.latestDate ?? "—"} sub={`${status ? nf(status.rates.currencyCount) : "—"} currencies`} />
        <StatCard label="PPP coverage" value={status?.ppp.years ? `${status.ppp.years.min}-${status.ppp.years.max}` : "—"} sub={`${status ? nf(status.ppp.rows) : "—"} rows · ${status ? nf(status.ppp.projected) : "—"} projected`} />
        <StatCard label="Organizations" value={status ? nf(status.counts.organizations) : "—"} sub={`${status ? nf(status.counts.apiKeys) : "—"} API keys`} />
        <StatCard label="Users" value={status ? nf(status.counts.users) : "—"} sub={`${status ? nf(status.counts.bannedUsers) : "—"} banned`} />
        <StatCard label="MCP usage" value={status ? nf(status.mcp.requestsThisPeriod) : "—"} sub={`${status ? nf(status.mcp.connectedOrgs) : "—"} orgs connected`} />
        <StatCard label="AI usage" value={status ? nf(status.ai.requestsThisPeriod) : "—"} sub="requests this period" />
      </div>

      {/* Action cards */}
      <div className="grid grid-3 action-grid">
        <ActionCard
          title="Exchange rates"
          body="Re-run the daily Frankfurter rate fetch and refresh the KV rate cache. Normally runs at 00:01 UTC."
          cta="Refresh rates now"
          loading={busy === "rates"}
          disabled={busy !== null}
          onClick={runRates}
        >
          {lastRates && (
            <p className="result-line">
              ✓ {lastRates.currencyCount} currencies · {lastRates.rowsWritten} rows written · latest {lastRates.latestDate}
            </p>
          )}
        </ActionCard>

        <div className="card card-pad action-card">
          <h3>Backfill a date</h3>
          <p className="muted">
            Fetch &amp; store rates for one specific (possibly past) date. Skips if that date
            already has data; never moves the latest pointer backward.
          </p>
          <div className="action-foot" style={{ display: "flex", gap: ".5rem", alignItems: "center", flexWrap: "wrap" }}>
            <DatePicker
              value={fetchDate}
              max={todayIso()}
              disabled={busy !== null}
              ariaLabel="Backfill date"
              onChange={setFetchDate}
            />
            <button
              className="btn btn-primary btn-sm"
              disabled={busy !== null || !fetchDate}
              onClick={runFetchForDate}
            >
              {busy === "rates-date" ? <><span className="spinner" />Working…</> : "Fetch & store"}
            </button>
          </div>
        </div>

        <div className="card card-pad action-card">
          <h3>Upload rates JSON</h3>
          <p className="muted">
            Ingest a <code>currencycore-rates/v1</code> export (from build-upload.mjs) to backfill
            any date / range. Writes D1 + KV; advances the latest pointer forward only. By default
            dates that already have data are skipped. Turn on force push to overwrite them.
          </p>
          <label style={{ display: "flex", gap: ".5rem", alignItems: "center", margin: ".4rem 0 .6rem" }}>
            <input
              type="checkbox"
              checked={forceRates}
              disabled={busy !== null}
              onChange={(e) => setForceRates(e.target.checked)}
            />
            <span>Force push: overwrite dates that already have data</span>
          </label>
          <div className="action-foot">
            <input ref={ratesFileRef} type="file" accept="application/json,.json" hidden onChange={onRatesFile} />
            <button
              className="btn btn-primary btn-sm"
              disabled={busy !== null}
              onClick={() => ratesFileRef.current?.click()}
            >
              {busy === "upload-rates" ? <><span className="spinner" />Working…</> : "Choose rates JSON…"}
            </button>
          </div>
          {lastRates && (
            <p className="result-line">
              ✓ {lastRates.written.length} date(s) · {nf(lastRates.rowsWritten)} rows · latest {lastRates.latestDate}
              {lastRates.skipped.length ? ` · ${lastRates.skipped.length} skipped` : ""}
            </p>
          )}
        </div>

        <ActionCard
          title="PPP factors (IMF)"
          body="Diff-update PPP factors from the live IMF PPPEX dataset. Upserts only changed values."
          cta="Sync PPP from IMF"
          loading={busy === "ppp"}
          disabled={busy !== null}
          onClick={runPpp}
        >
          {lastPpp && (
            <p className="result-line">
              ✓ {nf(lastPpp.changed)} changed · {nf(lastPpp.unchanged)} unchanged{lastPpp.years ? ` · ${lastPpp.years.min}-${lastPpp.years.max}` : ""}
            </p>
          )}
        </ActionCard>

        <ActionCard
          title="Upload PPP JSON"
          body="Ingest an IMF PPPEX export (ppp.json). Same diff engine, useful when the IMF edge blocks the Worker."
          cta="Choose ppp.json…"
          loading={busy === "upload"}
          disabled={busy !== null}
          onClick={() => fileRef.current?.click()}
        >
          <input ref={fileRef} type="file" accept="application/json,.json" hidden onChange={onFile} />
          <p className="hint">Expected: <code>{`{ indicators: { PPPEX: { values, projection } } }`}</code></p>
        </ActionCard>

        <ActionCard
          title="Homepage AI responses"
          body="Generate and cache AI answers for the 6 canonical homepage questions (hero card + AI queries section). Runs automatically at 15:00 UTC daily; use this to trigger it immediately."
          cta="Generate now"
          loading={busy === "homepage-ai"}
          disabled={busy !== null}
          onClick={runHomepageAI}
        />

        <div className="card card-pad action-card">
          <h3>AI endpoint</h3>
          <p className="muted">
            Turn the natural-language AI endpoint on or off platform-wide. When off, organizations
            can't enable it and users see the message below.
          </p>
          <label style={{ display: "flex", gap: ".5rem", alignItems: "center", margin: ".4rem 0 .6rem" }}>
            <input
              type="checkbox"
              checked={aiEnabled}
              disabled={aiBusy || !aiLoaded}
              onChange={(e) => setAiEnabled(e.target.checked)}
            />
            <span>AI endpoint enabled</span>
          </label>
          <label className="form-label" htmlFor="ai-msg">Disabled message (shown to users)</label>
          <textarea
            id="ai-msg"
            className="input"
            rows={2}
            value={aiMsg}
            disabled={aiBusy || !aiLoaded}
            placeholder="e.g. AI is paused for maintenance until 5pm UTC."
            onChange={(e) => setAiMsg(e.target.value)}
          />
          <div className="action-foot" style={{ marginTop: ".5rem" }}>
            <button className="btn btn-primary btn-sm" disabled={aiBusy || !aiLoaded} onClick={saveAi}>
              {aiBusy ? <><span className="spinner" />Saving…</> : "Save AI settings"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="card statbox">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      <span className="stat-sub muted">{sub}</span>
    </div>
  );
}

function ActionCard({
  title,
  body,
  cta,
  loading,
  disabled,
  onClick,
  children,
}: {
  title: string;
  body: string;
  cta: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="card card-pad action-card">
      <h3>{title}</h3>
      <p className="muted">{body}</p>
      <div className="action-foot">
        <button className="btn btn-primary btn-sm" disabled={disabled} onClick={onClick}>
          {loading ? <><span className="spinner" />Working…</> : cta}
        </button>
      </div>
      {children}
    </div>
  );
}

function errMsg(e: unknown, fallback: string): string {
  if (e instanceof ApiRequestError) return e.body.error.message || fallback;
  return e instanceof Error ? e.message : fallback;
}
